import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  hasMemberAccessToken,
  readStoredMemberUser,
  signOutMemberSession,
  subscribeToMemberSessionChanges
} from "@/features/public/memberSession";

const sportItems = [
  { label: "Calendrier", path: "/sport/calendrier", icon: "ri-calendar-line" },
  { label: "Résultats", path: "/sport/resultats", icon: "ri-bar-chart-line" },
  { label: "Événements", path: "/sport/evenements", icon: "ri-trophy-line" },
];

const communauteItems = [
  { label: "Équipes & Squads", path: "/communaute/squads", icon: "ri-team-line" },
  { label: "Forum", path: "/communaute/forum", icon: "ri-discuss-line" },
  { label: "Réseau Social", path: "/social", icon: "ri-global-line" },
  { label: "Cultures & Familles", path: "/culture", icon: "ri-parent-line" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sportOpen, setSportOpen] = useState(false);
  const [communauteOpen, setCommunauteOpen] = useState(false);
  const [mobileSportOpen, setMobileSportOpen] = useState(false);
  const [mobileCommunauteOpen, setMobileCommunauteOpen] = useState(false);
  const [memberName, setMemberName] = useState<string | null>(null);
  const [memberAvatar, setMemberAvatar] = useState<string | null>(null);
  const [memberInitials, setMemberInitials] = useState("M");
  const [hasMemberToken, setHasMemberToken] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const sportRef = useRef<HTMLDivElement>(null);
  const communauteRef = useRef<HTMLDivElement>(null);
  const sportCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const communauteCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openSport = () => {
    if (sportCloseTimer.current) clearTimeout(sportCloseTimer.current);
    setSportOpen(true);
  };
  const closeSportDelayed = () => {
    sportCloseTimer.current = setTimeout(() => setSportOpen(false), 300);
  };
  const openCommunaute = () => {
    if (communauteCloseTimer.current) clearTimeout(communauteCloseTimer.current);
    setCommunauteOpen(true);
  };
  const closeCommunauteDelayed = () => {
    communauteCloseTimer.current = setTimeout(() => setCommunauteOpen(false), 300);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const syncMemberState = useCallback(() => {
    const storedUser = readStoredMemberUser();
    const fullName = storedUser ? [storedUser.prenom, storedUser.nom].filter(Boolean).join(" ").trim() : null;
    const initials = storedUser
      ? [storedUser.prenom, storedUser.nom]
          .filter(Boolean)
          .map((value) => value.trim().charAt(0).toUpperCase())
          .join("")
          .slice(0, 2)
      : "M";

    setHasMemberToken(hasMemberAccessToken());
    setMemberName(fullName || "Membre");
    setMemberAvatar(storedUser?.avatar ?? null);
    setMemberInitials(initials || "M");
  }, []);

  const handleMemberLogout = useCallback(() => {
    void (async () => {
      try {
        await signOutMemberSession();
        setMemberName(null);
        setMemberAvatar(null);
        setMemberInitials("M");
        setHasMemberToken(false);
        navigate("/formulaires?tab=login");
      } catch {
        navigate("/formulaires?tab=login");
      }
    })();
  }, [navigate]);

  useEffect(() => {
    setMobileOpen(false);
    setSportOpen(false);
    setCommunauteOpen(false);
    syncMemberState();
  }, [location, syncMemberState]);

  useEffect(() => {
    syncMemberState();
    const handleStorage = () => syncMemberState();
    window.addEventListener("storage", handleStorage);
    const unsubscribe = subscribeToMemberSessionChanges(syncMemberState);

    return () => {
      window.removeEventListener("storage", handleStorage);
      unsubscribe();
    };
  }, [syncMemberState]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sportRef.current && !sportRef.current.contains(e.target as Node)) setSportOpen(false);
      if (communauteRef.current && !communauteRef.current.contains(e.target as Node)) setCommunauteOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const textColor = (active: boolean) =>
    active
      ? "text-[#E8521A]"
      : "text-[#2D2D2D] hover:text-[#E8521A]";

  return (
    <>
      <nav
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
          scrolled ? "shadow-sm" : "border-b border-gray-100"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 min-w-0">
            <img
              src="https://public.readdy.ai/ai/img_res/71bd3ce9-ea40-4eb5-8ec2-3e5ee5114aee.png"
              alt="ASC Sonatel"
              className="w-14 h-14 object-contain flex-shrink-0"
            />
            <div className="hidden sm:flex w-[112px] min-w-0 flex-col">
              <div className="font-bold text-lg leading-tight text-[#1A1A1A] whitespace-nowrap">
                ASC Sonatel
              </div>
              <div className="max-w-full text-[10px] leading-[1.15] text-[#1A8C7A]">
                Association Sportive & Culturelle
              </div>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Accueil */}
            <NavLink to="/" label="Accueil" className={textColor(location.pathname === "/")} />

            {/* Actualités */}
            <NavLink to="/actualites" label="Actualités" className={textColor(isActive("/actualites"))} />

            {/* Sport dropdown */}
            <DropdownMenu
              ref={sportRef}
              label="Sport"
              mainPath="/sport/calendrier"
              isOpen={sportOpen}
              isActive={isActive("/sport")}
              onMouseEnter={openSport}
              onMouseLeave={closeSportDelayed}
              onLabelClick={() => { navigate("/sport/calendrier"); setSportOpen(false); }}
              items={sportItems}
            />

            {/* Communauté dropdown */}
            <DropdownMenu
              ref={communauteRef}
              label="Communauté"
              mainPath="/communaute/squads"
              isOpen={communauteOpen}
              isActive={isActive("/communaute") || isActive("/social") || isActive("/culture")}
              onMouseEnter={openCommunaute}
              onMouseLeave={closeCommunauteDelayed}
              onLabelClick={() => { navigate("/communaute/squads"); setCommunauteOpen(false); }}
              items={communauteItems}
            />
          </div>

          {/* Right Actions — Espace Membre uniquement */}
          <div className="hidden lg:flex items-center gap-3">
            {hasMemberToken ? (
              <>
                <button
                  onClick={() => navigate("/membre")}
                  className="flex items-center gap-3 rounded-full border border-[#1A8C7A]/15 bg-[#F4FBF9] px-3 py-2.5 text-[#134E48] shadow-sm transition-all duration-200 hover:border-[#1A8C7A]/30 hover:bg-[#ECF9F5]"
                >
                  {memberAvatar ? (
                    <img src={memberAvatar} alt={memberName ?? "Membre"} className="h-11 w-11 rounded-full object-cover object-[center_24%] ring-2 ring-white" />
                  ) : (
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A8C7A] text-sm font-bold text-white ring-2 ring-white">
                      {memberInitials}
                    </div>
                  )}
                  <div className="min-w-0 text-left">
                    <div className="max-w-[140px] truncate text-sm font-semibold text-[#1A1A1A]">
                      {memberName ?? "Membre"}
                    </div>
                  </div>
                </button>
                <button
                  onClick={handleMemberLogout}
                  aria-label="Déconnexion"
                  title="Déconnexion"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-all duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                >
                  <i className="ri-logout-box-r-line text-xl"></i>
                </button>
              </>
            ) : (
              <Link
                to="/formulaires?tab=login&redirect=%2Fmembre&source=member-access"
                className="text-sm font-medium px-5 py-2 rounded-full bg-[#E8521A] text-white hover:bg-[#d44a16] transition-all duration-200 whitespace-nowrap"
              >
                <i className="ri-user-line mr-1.5"></i>
                Espace Membre
              </Link>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors text-[#1A1A1A]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-2xl transition-all duration-200 ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden bg-white border-t border-gray-100 shadow-lg overflow-hidden transition-all duration-300 ${
            mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-4 py-4 space-y-1">
            <MobileNavLink to="/" label="Accueil" icon="ri-home-line" />
            <MobileNavLink to="/actualites" label="Actualités" icon="ri-newspaper-line" />

            {/* Mobile Sport */}
            <MobileDropdown
              label="Sport"
              icon="ri-football-line"
              isOpen={mobileSportOpen}
              onToggle={() => setMobileSportOpen(!mobileSportOpen)}
              items={sportItems}
            />

            {/* Mobile Communauté */}
            <MobileDropdown
              label="Communauté"
              icon="ri-team-line"
              isOpen={mobileCommunauteOpen}
              onToggle={() => setMobileCommunauteOpen(!mobileCommunauteOpen)}
              items={communauteItems}
            />

            <div className="pt-3 border-t border-gray-100">
              {hasMemberToken ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate("/membre")}
                    className="flex min-w-0 flex-1 items-center gap-3 rounded-full border border-[#1A8C7A]/15 bg-[#F4FBF9] px-3 py-2.5 text-left text-[#134E48] shadow-sm transition-colors hover:bg-[#ECF9F5]"
                  >
                    {memberAvatar ? (
                      <img src={memberAvatar} alt={memberName ?? "Membre"} className="h-11 w-11 rounded-full object-cover object-[center_24%] ring-2 ring-white" />
                    ) : (
                      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A8C7A] text-sm font-bold text-white ring-2 ring-white">
                        {memberInitials}
                      </div>
                    )}
                    <div className="min-w-0 text-left">
                      <div className="truncate text-sm font-semibold text-[#1A1A1A]">{memberName ?? "Membre"}</div>
                    </div>
                  </button>
                  <button
                    onClick={handleMemberLogout}
                    aria-label="Déconnexion"
                    title="Déconnexion"
                    className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-all duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  >
                    <i className="ri-logout-box-r-line text-xl"></i>
                  </button>
                </div>
              ) : (
                <Link
                  to="/formulaires?tab=login&redirect=%2Fmembre&source=member-access"
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors"
                >
                  <i className="ri-user-line"></i>
                  Espace Membre
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

/* ─── Desktop Dropdown ─── */
import { forwardRef } from "react";

interface DropdownMenuProps {
  label: string;
  mainPath: string;
  isOpen: boolean;
  isActive: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onLabelClick: () => void;
  items: { label: string; path: string; icon: string }[];
}

const DropdownMenu = forwardRef<HTMLDivElement, DropdownMenuProps>(
  ({ label, isOpen, isActive, onMouseEnter, onMouseLeave, onLabelClick, items }, ref) => (
    <div ref={ref} className="relative">
      <div
        className="flex items-center"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* Label cliquable → navigue directement */}
        <button
          className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
            isActive
              ? "text-[#E8521A]"
              : "text-[#2D2D2D] hover:text-[#E8521A]"
          }`}
          onClick={onLabelClick}
        >
          {label}
        </button>
        {/* Chevron seul → ouvre/ferme le dropdown */}
        <button
          className={`flex items-center justify-center w-6 h-6 rounded transition-all duration-200 cursor-pointer ${
            isActive ? "text-[#E8521A]" : "text-[#2D2D2D] hover:text-[#E8521A]"
          }`}
          onClick={(e) => { e.stopPropagation(); onMouseEnter(); }}
        >
          <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}></i>
        </button>
      </div>

      <div
        className={`absolute top-full left-0 mt-1 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 transition-all duration-200 origin-top ${
          isOpen ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {items.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="block px-4 py-3 text-sm text-[#2D2D2D] hover:text-[#E8521A] hover:bg-orange-50 transition-colors"
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  )
);
DropdownMenu.displayName = "DropdownMenu";

/* ─── Desktop NavLink ─── */
function NavLink({ to, label, className }: { to: string; label: string; className: string }) {
  return (
    <Link to={to} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${className}`}>
      {label}
    </Link>
  );
}

/* ─── Mobile NavLink ─── */
function MobileNavLink({ to, label, icon }: { to: string; label: string; icon: string }) {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + "/");
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
        isActive ? "bg-orange-50 text-[#E8521A]" : "text-[#2D2D2D] hover:text-[#E8521A] hover:bg-orange-50"
      }`}
    >
      <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${isActive ? "bg-orange-100" : "bg-gray-100"}`}>
        <i className={`${icon} text-base ${isActive ? "text-[#E8521A]" : "text-gray-500"}`}></i>
      </span>
      {label}
    </Link>
  );
}

/* ─── Mobile Dropdown ─── */
function MobileDropdown({
  label,
  icon,
  isOpen,
  onToggle,
  items,
}: {
  label: string;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
  items: { label: string; path: string; icon: string }[];
}) {
  return (
    <div>
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#2D2D2D] hover:text-[#E8521A] hover:bg-orange-50 rounded-xl transition-colors"
        onClick={onToggle}
      >
        <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
          <i className={`${icon} text-base text-gray-500`}></i>
        </span>
        <span className="flex-1 text-left">{label}</span>
        <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}></i>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="ml-11 mt-1 space-y-1 pb-1">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="block px-4 py-2.5 text-sm text-gray-600 hover:text-[#E8521A] hover:bg-orange-50 rounded-xl transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
